import { CheckField, CheckGroupField } from '@ff-it/form';
import { openModal, RenderProps } from '@ff-it/ui';
import { BlockWidget, DialogForm } from 'components';
import { type PlanState } from '../views/Plan/types';
import { api } from 'services';
import { type ListBlock } from '../types';
import { type ReactNode } from 'react';
import invariant from 'tiny-invariant';
import { cancelExport } from '../views/Plan/const';

type ExportOptions = {
  blocks: number[];
  show_groups: boolean;
};

const intialValues: ExportOptions = { blocks: [], show_groups: true };

function PlanExportOptionsDialog({
  siblings,
  hasGroups,
  ...props
}: RenderProps<ExportOptions | undefined> & {
  siblings: ListBlock[];
  hasGroups: boolean;
}) {
  return (
    <DialogForm {...props} title="Plan export" initialValues={intialValues}>
      {siblings.length > 0 && (
        <CheckGroupField
          name="blocks"
          options={siblings.map((block) => ({
            value: block.id.toString(),
            label: (<BlockWidget {...block} flat />) as ReactNode,
          }))}
          label="Include sibling plans"
        />
      )}
      {hasGroups && (
        <CheckField name="show_groups" label="Show groups" fieldLabel="Render group rows" variant="switch" />
      )}
    </DialogForm>
  );
}

export async function promptExportOptions({
  campaign_id,
  id,
  groups,
  plan_strategy,
}: PlanState): Promise<ExportOptions | undefined | typeof cancelExport> {
  if (plan_strategy === 'NAIVE') {
    return undefined;
  }
  const res = await api.request<ListBlock[], unknown>({
    method: 'GET',
    url: 'campaign/blocks/',
    queryParams: {
      campaign: campaign_id,
      exclude: id,
      plan_strategy: plan_strategy,
      ordering: 'order',
    },
  });
  invariant(res.ok);

  const hasGroups = groups.length > 0;
  const sibilings = res.data;

  let exportOptions;
  if (sibilings.length || hasGroups) {
    exportOptions = await openModal<ExportOptions>(
      (props) => <PlanExportOptionsDialog siblings={sibilings} hasGroups={hasGroups} {...props} />,
      {
        testId: 'plan-export-options',
      },
    );
    if (typeof exportOptions === 'undefined') {
      return cancelExport;
    }
    return exportOptions;
  }
}
